import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { CtRateNewRegularCard } from "../components/CtRateNewRegularCard";
import {
  BarChart3, Download, Filter, TrendingUp, PieChart as PieChartIcon,
  Calendar, Building2, Loader2, FileText, Printer, Gavel
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { BranchFilterSelect } from "../components/Workspace";
import { readWorkspace, branchDisplay } from "../utils/workspace";
import { StatsScopeGate, StatsScopeBadge, readStatsScope, saveStatsScope } from "../components/StatsScope";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
  ComposedChart
} from "recharts";

const COLORS = [
  "#1e40af", "#0d9488", "#d97706", "#dc2626", "#7c3aed",
  "#059669", "#e11d48", "#0284c7", "#ca8a04", "#6366f1",
  "#16a34a", "#ea580c", "#8b5cf6", "#0891b2", "#be185d"
];

const Statistics = () => {
  const { user } = useAuth();
  const courtBranch = user?.court_branch || "Πρωτοδικείο Αθηνών";
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");
  // user (Νοέ 2026): υποχρεωτική επιλογή ΜΟΝΟ Καταστήματος + Τύπου πριν τη φόρτωση — τα υπόλοιπα φίλτρα όπως ήταν
  const [scope, setScope] = useState(() => readStatsScope(user));
  const [editingScope, setEditingScope] = useState(false);
  const [courtFilter, setCourtFilter] = useState(() => scope ? (scope.court_type || "all") : (userCourtTypes.length === 1 ? userCourtTypes[0] : (readWorkspace(user)?.court_type || "all")));
  const [branchFilter, setBranchFilter] = useState(() => scope ? (scope.branch || "") : (readWorkspace(user)?.branch || ""));
  const confirmScope = (next) => {
    setScope(saveStatsScope(user, next));
    setBranchFilter(next.branch || "");
    setCourtFilter(next.court_type || "all");
    setEditingScope(false);
  };
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [pretrialProgress, setPretrialProgress] = useState(null);
  const [ctRateNewRegular, setCtRateNewRegular] = useState(null);
  const [years, setYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [casesByYear, setCasesByYear] = useState([]);
  const [casesBySubject, setCasesBySubject] = useState([]);
  const [casesByCourtType, setCasesByCourtType] = useState([]);
  const [casesByStatus, setCasesByStatus] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [comparativeYears, setComparativeYears] = useState([]);
  const [ctRateData, setCtRateData] = useState([]);
  const [caseFlags, setCaseFlags] = useState(null);
  const chartsRef = useRef(null);
  const chartRefs = useRef({});

  const fetchFilters = useCallback(async () => {
    try {
      const [yearsRes, subjectsRes] = await Promise.all([
        axios.get(`${API}/stats/cases-by-year`),
        axios.get(`${API}/cases/subjects`)
      ]);
      setYears(yearsRes.data.map(y => y.year).filter(Boolean));
      setSubjects(subjectsRes.data || []);
    } catch (error) {
      console.error("Statistics: failed to fetch filters", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    if (!scope) { setLoading(false); return; }
    try {
      setLoading(true);
      const params = {};
      if (yearFilter !== "all") params.year = yearFilter;
      if (courtFilter !== "all") params.court_type = courtFilter;
      if (subjectFilter !== "all") params.subject = subjectFilter;
      const br = branchFilter || undefined;

      const [byYear, bySubject, byCourt, byStatus, monthly, comparative, ctRate, flags, progress, ctNew] = await Promise.all([
        axios.get(`${API}/stats/cases-by-year`, { params: { court_type: params.court_type, subject: params.subject, branch: br } }),
        axios.get(`${API}/stats/cases-by-subject`, { params: { court_type: params.court_type, year: params.year, branch: br } }),
        axios.get(`${API}/stats/cases-by-court-type`, { params: { year: params.year, subject: params.subject, branch: br } }),
        axios.get(`${API}/stats/cases-by-status`, { params: { year: params.year, court_type: params.court_type, branch: br } }),
        axios.get(`${API}/stats/monthly-trend`, { params: { year: params.year, court_type: params.court_type, branch: br } }),
        axios.get(`${API}/stats/comparative-years`, { params: { court_type: params.court_type, subject: params.subject, branch: br } }),
        axios.get(`${API}/stats/ct-rate`, { params: { court_type: params.court_type, subject: params.subject, branch: br } }),
        axios.get(`${API}/stats/case-flags`, { params: { year: params.year, court_type: params.court_type, subject: params.subject, branch: br } }),
        axios.get(`${API}/stats/pretrial-progress`, { params: { year: (params.year && params.year >= "2026") ? params.year : "2026", court_type: params.court_type, branch: br } }),
        axios.get(`${API}/stats/ct-rate-new-regular`, { params: { branch: br } })
      ]);
      setCaseFlags(flags.data);
      setCtRateNewRegular(ctNew.data);
      setPretrialProgress(progress.data);

      setCasesByYear(byYear.data);
      setCasesBySubject(bySubject.data.slice(0, 12));
      setCasesByCourtType(byCourt.data);
      setCasesByStatus(byStatus.data);
      setMonthlyTrend(monthly.data);
      setComparativeYears(comparative.data);
      setCtRateData(ctRate.data);
    } catch {
      toast.error("Σφάλμα φόρτωσης στατιστικών");
    } finally {
      setLoading(false);
    }
  }, [yearFilter, courtFilter, subjectFilter, branchFilter, !!scope]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const totalCases = casesByCourtType.reduce((sum, c) => sum + c.count, 0);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = 297, pageH = 210, margin = 10;
      const contentW = pageW - margin * 2, contentH = pageH - margin * 2;

      const branchName = courtFilter !== "all" ? courtFilter : "\u03A0\u03C1\u03C9\u03C4\u03BF\u03B4\u03B9\u03BA\u03B5\u03AF\u03BF \u0391\u03B8\u03B7\u03BD\u03CE\u03BD";
      const filterText = [
        courtFilter !== "all" ? `\u03A4\u03CD\u03C0\u03BF\u03C2: ${courtFilter}` : null,
        yearFilter !== "all" ? `\u0388\u03C4\u03BF\u03C2: ${yearFilter}` : null,
        subjectFilter !== "all" ? `\u0391\u03BD\u03C4\u03B9\u03BA\u03B5\u03AF\u03BC\u03B5\u03BD\u03BF: ${subjectFilter}` : null
      ].filter(Boolean).join(" | ") || "\u038C\u03BB\u03B1 \u03C4\u03B1 \u03B4\u03B5\u03B4\u03BF\u03BC\u03AD\u03BD\u03B1";

      // Title page - render as HTML for Greek font support
      const titleDiv = document.createElement("div");
      titleDiv.style.cssText = "position:fixed;top:-9999px;left:0;width:1100px;height:600px;background:#fff;padding:40px;font-family:'Segoe UI',Tahoma,Arial,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;";
      titleDiv.innerHTML = `
        <div style="text-align:center;">
          <h1 style="font-size:48px;color:#1e3a5f;margin:0 0 16px 0;font-weight:700;">${courtBranch}</h1>
          <h2 style="font-size:28px;color:#1e3a5f;margin:0 0 24px 0;font-weight:400;">${branchName}</h2>
          <p style="font-size:18px;color:#64748b;margin:0 0 12px 0;">${filterText}</p>
          <p style="font-size:18px;color:#64748b;margin:0;">\u03A3\u03CD\u03BD\u03BF\u03BB\u03BF \u03A5\u03C0\u03BF\u03B8\u03AD\u03C3\u03B5\u03C9\u03BD: ${totalCases} | ${new Date().toLocaleDateString("el-GR")}</p>
        </div>
      `;
      document.body.appendChild(titleDiv);
      try {
        const titleCanvas = await html2canvas(titleDiv, { scale: 2, backgroundColor: "#ffffff", logging: false });
        const titleImg = titleCanvas.toDataURL("image/jpeg", 0.95);
        const titleRatio = titleCanvas.width / titleCanvas.height;
        let tw = contentW, th = contentW / titleRatio;
        if (th > contentH) { th = contentH; tw = contentH * titleRatio; }
        doc.addImage(titleImg, "JPEG", margin + (contentW - tw) / 2, margin + (contentH - th) / 2, tw, th);
      } finally {
        document.body.removeChild(titleDiv);
      }

      // Capture each chart using html2canvas on the CARD element (not SVG)
      const chartKeys = ["pretrial", "flags", "year", "subject", "trend", "court", "ctrate", "ctrate_nt", "comparative"];
      for (const key of chartKeys) {
        const el = chartRefs.current[key];
        if (!el) continue;
        try {
          // Force a fixed width for consistent rendering
          const origWidth = el.style.width;
          el.style.width = "1100px";
          el.style.minHeight = "500px";
          
          // Wait for reflow
          await new Promise(r => setTimeout(r, 300));
          
          const canvas = await html2canvas(el, {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            width: 1100,
            windowWidth: 1200,
            removeContainer: true,
          });
          
          // Restore original width
          el.style.width = origWidth;
          el.style.minHeight = "";
          
          const imgData = canvas.toDataURL("image/jpeg", 0.90);
          const imgRatio = canvas.width / canvas.height;
          
          // Scale to fit A4 landscape with margins
          let imgW = contentW;
          let imgH = contentW / imgRatio;
          if (imgH > contentH) {
            imgH = contentH;
            imgW = contentH * imgRatio;
          }
          
          doc.addPage();
          const xOff = margin + (contentW - imgW) / 2;
          const yOff = margin + (contentH - imgH) / 2;
          doc.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        } catch (e) {
          console.warn("Chart capture failed:", key, e);
        }
      }

      doc.save("Themis_Pro_Statistika.pdf");
      toast.success("Το PDF αποθηκεύτηκε!");
    } catch (e) {
      console.error("PDF export error:", e);
      toast.error("Σφάλμα: " + (e.message || "PDF export"));
    } finally {
      setExporting(false);
    }
  };

  const printSingleChart = async (chartRef) => {
    if (!chartRef) return;
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;
      chartRef.style.width = "1100px";
      chartRef.style.minHeight = "500px";
      await new Promise(r => setTimeout(r, 300));
      const canvas = await html2canvas(chartRef, {
        scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false, width: 1100, windowWidth: 1200
      });
      chartRef.style.width = "";
      chartRef.style.minHeight = "";
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const imgData = canvas.toDataURL("image/jpeg", 0.90);
      const imgRatio = canvas.width / canvas.height;
      const margin = 10, contentW = 277, contentH = 190;
      let imgW = contentW, imgH = contentW / imgRatio;
      if (imgH > contentH) { imgH = contentH; imgW = contentH * imgRatio; }
      doc.addImage(imgData, "JPEG", margin + (contentW - imgW) / 2, margin + (contentH - imgH) / 2, imgW, imgH);
      doc.save("Themis_Pro_Chart.pdf");
      toast.success("Αποθηκεύτηκε!");
    } catch { toast.error("Σφάλμα"); }
  };

  const statusLabels = { active: "Ενεργές", closed: "Κλειστές", archived: "Αρχειοθετημένες", pending: "Εκκρεμείς" };

  const ChartPrintBtn = ({ chartKey }) => (
    <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
      onClick={() => printSingleChart(chartRefs.current[chartKey])}
      title="Εκτύπωση γραφήματος" data-testid={`print-chart-${chartKey}`}>
      <Printer className="w-3.5 h-3.5 text-slate-400" />
    </Button>
  );

  if (loading && casesByYear.length === 0) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div><Skeleton className="h-8 w-48 mb-2" /><Skeleton className="h-5 w-72" /></div>
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-80 w-full rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!scope || editingScope) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Στατιστικά</h1>
          <p className="text-slate-500 mt-1">Επιλέξτε Κατάστημα και Τύπο Δικαστηρίου για να φορτωθούν τα στατιστικά</p>
        </div>
        <StatsScopeGate user={user} initial={scope} onConfirm={confirmScope} onCancel={scope ? () => setEditingScope(false) : undefined} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Στατιστικά</h1>
          <p className="text-slate-500 mt-1">Ανάλυση υποθέσεων με δυναμικά φίλτρα και γραφήματα</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatsScopeBadge scope={{ branch: branchFilter, court_type: courtFilter === "all" ? "" : courtFilter }} onChange={() => setEditingScope(true)} />
          <Button onClick={handleExportPdf} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Εξαγωγή PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Filter className="w-4 h-4" />Φίλτρα:
            </div>
            <BranchFilterSelect value={branchFilter} onChange={setBranchFilter} className="w-full md:w-60" testId="branch-filter-stats" />
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="year-filter">
                <SelectValue placeholder="Έτος" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλα τα έτη</SelectItem>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={courtFilter} onValueChange={setCourtFilter}
              disabled={user?.role !== "admin" && userCourtTypes.length === 1}>
              <SelectTrigger className="w-full md:w-44" data-testid="court-type-filter">
                <SelectValue placeholder="Τύπος Δικαστηρίου" />
              </SelectTrigger>
              <SelectContent>
                {(user?.role === "admin" || userCourtTypes.length > 1) && (
                  <SelectItem value="all">Όλοι οι τύποι</SelectItem>
                )}
                {(user?.role === "admin" ? ["Μονομελές", "Πολυμελές"] : userCourtTypes).map(ct => (
                  <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={subjectFilter} onValueChange={setSubjectFilter}>
              <SelectTrigger className="w-full md:w-56" data-testid="subject-filter">
                <SelectValue placeholder="Αντικείμενο" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλα τα αντικείμενα</SelectItem>
                {subjects.map(s => <SelectItem key={s} value={s}>{s.length > 35 ? s.substring(0,35) + "..." : s}</SelectItem>)}
              </SelectContent>
            </Select>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalCases}</p>
              <p className="text-xs text-slate-500">Σύνολο Υποθέσεων</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{years.length}</p>
              <p className="text-xs text-slate-500">Έτη</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <PieChartIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{casesBySubject.length}</p>
              <p className="text-xs text-slate-500">Αντικείμενα</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{casesByCourtType.length}</p>
              <p className="text-xs text-slate-500">Τύποι Δικαστηρίου</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div ref={chartsRef} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cases by Year - Bar Chart */}
          <Card ref={el => chartRefs.current["year"] = el}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  Υποθέσεις ανά Έτος
                </CardTitle>
                <ChartPrintBtn chartKey="year" />
              </div>
            </CardHeader>
            <CardContent>
              {casesByYear.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={casesByYear}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="year" tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" }} />
                    <YAxis tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" }} />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="count" name="Υποθέσεις" fill="#1e40af" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-center text-slate-400 py-12">Δεν υπάρχουν δεδομένα</p>}
            </CardContent>
          </Card>

          {/* Cases by Subject - Pie Chart */}
          <Card ref={el => chartRefs.current["subject"] = el}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4 text-teal-600" />
                  Κατανομή ανά Αντικείμενο
                </CardTitle>
                <ChartPrintBtn chartKey="subject" />
              </div>
            </CardHeader>
            <CardContent>
              {casesBySubject.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={casesBySubject} dataKey="count" nameKey="subject" cx="50%" cy="50%"
                      outerRadius={100} innerRadius={40} paddingAngle={2} label={({ subject, percent }) =>
                        `${subject?.substring(0, 15)}${subject?.length > 15 ? "..." : ""} (${(percent * 100).toFixed(0)}%)`
                      } labelLine={{ strokeWidth: 1 }}>
                      {casesBySubject.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-center text-slate-400 py-12">Δεν υπάρχουν δεδομένα</p>}
            </CardContent>
          </Card>

          {/* Monthly Trend - Area Chart */}
          <Card ref={el => chartRefs.current["trend"] = el}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  Μηνιαία Τάση {yearFilter !== "all" ? `(${yearFilter})` : ""}
                </CardTitle>
                <ChartPrintBtn chartKey="trend" />
              </div>
            </CardHeader>
            <CardContent>
              {monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyTrend}>
                    <defs>
                      <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} />
                    <YAxis tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                    <Area type="monotone" dataKey="count" name="Υποθέσεις" stroke="#059669" fill="url(#colorCount)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="text-center text-slate-400 py-12">Δεν υπάρχουν δεδομένα</p>}
            </CardContent>
          </Card>

          {/* Court Type Distribution */}
          <Card ref={el => chartRefs.current["court"] = el}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-600" />
                  Κατανομή ανά Τύπο Δικαστηρίου
                </CardTitle>
                <ChartPrintBtn chartKey="court" />
              </div>
            </CardHeader>
            <CardContent>
              {casesByCourtType.length > 0 ? (
                <div className="space-y-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={casesByCourtType} dataKey="count" nameKey="court_type" cx="50%" cy="50%"
                        outerRadius={80} innerRadius={50} paddingAngle={4}
                        label={({ court_type, percent }) => `${court_type} (${(percent * 100).toFixed(0)}%)`}>
                        {casesByCourtType.map((_, i) => <Cell key={i} fill={i === 0 ? "#1e40af" : "#0d9488"} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-6">
                    {casesByCourtType.map((ct, i) => (
                      <div key={ct.court_type} className="text-center">
                        <p className="text-2xl font-bold" style={{ color: i === 0 ? "#1e40af" : "#0d9488" }}>{ct.count}</p>
                        <p className="text-sm text-slate-500">{ct.court_type}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p className="text-center text-slate-400 py-12">Δεν υπάρχουν δεδομένα</p>}
            </CardContent>
          </Card>
        </div>

        {/* CT Rate - Ρυθμός Εκκαθάρισης */}
        {ctRateData.length > 0 && (
          <Card ref={el => chartRefs.current["ctrate"] = el}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  Ρυθμός Εκκαθάρισης (CT Rate)
                </CardTitle>
                <ChartPrintBtn chartKey="ctrate" />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Ανά ημερολογιακό έτος: Εκκαθαρισμένες = υποθέσεις που έλαβαν δικάσιμο εντός του έτους (ανεξαρτήτως έτους κατάθεσης) προς Εισερχόμενες του έτους. CT Rate {">"} 100% = μείωση εκκρεμότητας. Πληροφοριακά: πόσες από τις εισερχόμενες κάθε έτους έχουν/δεν έχουν δικάσιμο.
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={ctRateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} unit="%" domain={[0, 'auto']} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar yAxisId="left" dataKey="incoming" name="Εισερχόμενες" fill="#1e40af" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="resolved" name="Εκκαθαρισμένες (έλαβαν δικάσιμο εντός έτους)" fill="#059669" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="unscheduled" name="Χωρίς δικάσιμο (από τις εισερχόμενες)" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="ct_rate" name="CT Rate %" stroke="#dc2626" strokeWidth={2} dot={{ fill: "#dc2626", r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm" data-testid="ct-rate-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-700">Έτος</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Εισερχόμενες</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Εκκαθαρισμένες<br /><span className="font-normal text-xs text-slate-500">έλαβαν δικάσιμο εντός έτους</span></th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">CT Rate</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Με δικάσιμο<br /><span className="font-normal text-xs text-slate-500">από τις εισερχόμενες</span></th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Χωρίς δικάσιμο<br /><span className="font-normal text-xs text-slate-500">από τις εισερχόμενες</span></th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Εκκρεμείς (σωρ.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ctRateData.map(row => (
                      <tr key={row.year} className="border-b border-slate-100" data-testid={`ct-rate-row-${row.year}`}>
                        <td className="py-2 px-3 font-medium">{row.year}</td>
                        <td className="py-2 px-3 text-right text-blue-700 font-medium">{row.incoming}</td>
                        <td className="py-2 px-3 text-right text-emerald-700 font-medium">{row.resolved}</td>
                        <td className="py-2 px-3 text-right">
                          {row.ct_rate === null || row.ct_rate === undefined ? (
                            <span className="text-slate-400" data-testid={`ct-rate-value-${row.year}`}>—</span>
                          ) : (
                            <span className={`font-bold ${row.ct_rate >= 100 ? "text-emerald-600" : row.ct_rate >= 80 ? "text-amber-600" : "text-red-600"}`} data-testid={`ct-rate-value-${row.year}`}>
                              {row.ct_rate}%
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right text-amber-700 font-medium">{row.scheduled} <span className="text-xs text-slate-500">({row.scheduled_pct}%)</span></td>
                        <td className="py-2 px-3 text-right text-slate-500 font-medium">{row.unscheduled} <span className="text-xs text-slate-400">({row.incoming ? Math.round((100 - row.scheduled_pct) * 10) / 10 : 0}%)</span></td>
                        <td className="py-2 px-3 text-right text-slate-600">{row.pending}</td>
                      </tr>
                    ))}
                    {ctRateData.length > 1 && (() => {
                      const t = ctRateData.reduce((a, r) => ({ incoming: a.incoming + r.incoming, resolved: a.resolved + r.resolved, scheduled: a.scheduled + (r.scheduled || 0), unscheduled: a.unscheduled + (r.unscheduled || 0) }), { incoming: 0, resolved: 0, scheduled: 0, unscheduled: 0 });
                      const rate = t.incoming ? Math.round(t.resolved / t.incoming * 1000) / 10 : 0;
                      const sp = t.incoming ? Math.round(t.scheduled / t.incoming * 1000) / 10 : 0;
                      return (
                        <tr className="bg-slate-50 font-semibold" data-testid="ct-rate-total-row">
                          <td className="py-2 px-3">Σύνολο</td>
                          <td className="py-2 px-3 text-right text-blue-800">{t.incoming}</td>
                          <td className="py-2 px-3 text-right text-emerald-800">{t.resolved}</td>
                          <td className="py-2 px-3 text-right"><span className={`font-bold ${rate >= 100 ? "text-emerald-600" : rate >= 80 ? "text-amber-600" : "text-red-600"}`}>{rate}%</span></td>
                          <td className="py-2 px-3 text-right text-amber-800">{t.scheduled} <span className="text-xs text-slate-500">({sp}%)</span></td>
                          <td className="py-2 px-3 text-right text-slate-600">{t.unscheduled} <span className="text-xs text-slate-400">({t.incoming ? Math.round((100 - sp) * 10) / 10 : 0}%)</span></td>
                          <td className="py-2 px-3 text-right text-slate-700">{Math.max(0, t.incoming - t.resolved)}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CT Rate — ΜΟΝΟ Νέα Τακτική (user 2026-10-19), ακριβώς κάτω από τον γενικό CT Rate */}
        <CtRateNewRegularCard ref={el => chartRefs.current["ctrate_nt"] = el} data={ctRateNewRegular} />

        {/* Comparative Table */}
        {comparativeYears.length > 0 && (
          <Card ref={el => chartRefs.current["comparative"] = el}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-600" />
                  Συγκριτικός Πίνακας ανά Έτος
                </CardTitle>
                <ChartPrintBtn chartKey="comparative" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Έτος</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Σύνολο</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Κορυφαία Αντικείμενα</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Μεταβολή</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativeYears.sort((a, b) => (b.year || "").localeCompare(a.year || "")).map((yearData, idx) => {
                      const prevYear = comparativeYears.find(y => y.year === String(parseInt(yearData.year) - 1));
                      const change = prevYear ? ((yearData.total - prevYear.total) / prevYear.total * 100).toFixed(1) : null;
                      const topSubjects = Object.entries(yearData.subjects || {}).sort((a, b) => b[1] - a[1]).slice(0, 3);

                      return (
                        <tr key={yearData.year} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium text-slate-900">{yearData.year}</td>
                          <td className="py-3 px-4 text-right font-semibold text-slate-900">{yearData.total}</td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {topSubjects.map(([subj, count]) => (
                                <Badge key={subj} variant="outline" className="text-xs">
                                  {subj?.substring(0, 20)}{subj?.length > 20 ? "..." : ""}: {count}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {change !== null ? (
                              <span className={`font-medium ${parseFloat(change) > 0 ? "text-emerald-600" : parseFloat(change) < 0 ? "text-red-600" : "text-slate-500"}`}>
                                {parseFloat(change) > 0 ? "+" : ""}{change}%
                              </span>
                            ) : <span className="text-slate-400">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Πρόοδος Προδικαστικού Ελέγχου (iter109) */}
        {pretrialProgress && (
          <Card ref={el => chartRefs.current["pretrial"] = el} data-testid="pretrial-progress-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Gavel className="w-4 h-4 text-blue-700" />
                Πρόοδος Προδικαστικού Ελέγχου {pretrialProgress.year} — {branchDisplay(branchFilter)}{courtFilter !== "all" ? ` · ${courtFilter}` : ""}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pretrial-progress-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b">
                      <th className="py-2 pr-4">Αγωγές / Κλήσεις / Κύριες Παρεμβάσεις που κατατέθηκαν</th>
                      <th className="py-2 pr-4">Προδικαστικοί που ολοκληρώθηκαν</th>
                      <th className="py-2 pr-4">Αναμένονται εντός 15 ημερών</th>
                      <th className="py-2 pr-4">Απομένουν</th>
                      <th className="py-2 pr-4">Χωρίς ορισμένο δικαστή</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="font-semibold text-slate-900">
                      <td className="py-2 pr-4" data-testid="pp-total">{pretrialProgress.total}</td>
                      <td className="py-2 pr-4 text-emerald-700" data-testid="pp-completed">{pretrialProgress.completed}</td>
                      <td className="py-2 pr-4 text-amber-700" data-testid="pp-expected">{pretrialProgress.expected_15d}</td>
                      <td className="py-2 pr-4 text-blue-700" data-testid="pp-remaining">{pretrialProgress.remaining}</td>
                      <td className="py-2 pr-4 text-slate-600" data-testid="pp-no-judge">{pretrialProgress.no_judge}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Ολοκληρωμένος = σήμανση δικαστή ({pretrialProgress.breakdown?.judge_marked || 0}) · παρήλθε το 30ήμερο ({pretrialProgress.breakdown?.period_elapsed || 0}) · Διάταξη SOLON/χρήστη ({pretrialProgress.breakdown?.order_issued || 0}) · επιτυχής διαμεσολάβηση ({pretrialProgress.breakdown?.mediation_success || 0}) · 214Α ({pretrialProgress.breakdown?.settlement_214a || 0}). Στοιχεία από SOLON ή/και καταχώριση χρήστη, ημ/νία αναφοράς {pretrialProgress.as_of}.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Δικονομικά κριτήρια Ναι/Όχι */}
        {caseFlags && (
          <Card ref={el => chartRefs.current["flags"] = el} data-testid="case-flags-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  Διάταξη · Παραίτηση · Διαμεσολάβηση · 214Α
                </CardTitle>
                <ChartPrintBtn chartKey="flags" />
              </div>
              <p className="text-sm text-slate-500 mt-1" data-testid="case-flags-denominator">
                Ποσοστά επί των <b>{caseFlags.total}</b> χρονικά ολοκληρωμένων προδικαστικών ελέγχων του τμήματος (υπαγόμενες υποθέσεις με παρελθόν 30ήμερο δικαστή ή σήμανση ολοκλήρωσης)
                {typeof caseFlags.eligible_cases === "number" && <> — από {caseFlags.eligible_cases} υπαγόμενες / {caseFlags.all_cases} συνολικά υποθέσεις του φίλτρου</>}.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseFlags.items.map(it => (
                  <div key={it.key} className="rounded-lg border border-slate-200 p-3" data-testid={`case-flag-${it.key}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-800">{it.label}</span>
                      <span className="text-slate-500">{it.pct}%</span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded bg-slate-100 overflow-hidden">
                      <div className="h-2 bg-indigo-600" style={{ width: `${Math.min(100, it.pct)}%` }} />
                    </div>
                    <div className="mt-2 flex gap-4 text-xs">
                      <span className="text-emerald-700">Ναι: <b data-testid={`case-flag-${it.key}-yes`}>{it.yes}</b></span>
                      <span className="text-slate-500">Όχι: <b data-testid={`case-flag-${it.key}-no`}>{it.no}</b></span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subject Breakdown Bar Chart */}
        {casesBySubject.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-amber-600" />
                Ανάλυση ανά Αντικείμενο
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(300, casesBySubject.length * 35)}>
                <BarChart data={casesBySubject} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} />
                  <YAxis type="category" dataKey="subject" tick={{ fontSize: 11, fontFamily: "'Segoe UI', Tahoma, sans-serif" }} width={120}
                    tickFormatter={v => v?.length > 25 ? v.substring(0, 25) + "..." : v} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="count" name="Υποθέσεις" radius={[0, 4, 4, 0]}>
                    {casesBySubject.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Statistics;
